import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  isFirstVisit,
  isNewBuild,
  rememberBuild,
} from '../../version/core/seenBuild';
import { whatsNewEntries } from '../data/whatsNew';
import { WhatsNewList } from './WhatsNewList';

/*
 * What the dashboard card says, said to whoever just got a new build —
 * once, and where they are standing. The card only reaches those who
 * happen to open the overview; a release nobody notices might as well
 * not have been written about.
 *
 * A native <dialog> like the delete confirmation: it dims the page and
 * closes on Escape. jsdom implements neither showModal() nor close(),
 * hence the guards — there the markup simply renders inline.
 */
export function WhatsNewDialog() {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  /* asked afresh on every mounting, and the same answer every time:
     nothing here writes the build down (see seenBuild) */
  const [isOpen, setIsOpen] = useState(
    () => isNewBuild() && whatsNewEntries.length > 0
  );

  /* the first visit says nothing but has to leave its mark: without a
     build written down, the next visit would be a first one as well */
  useEffect(() => {
    if (isFirstVisit()) rememberBuild();
  }, []);

  /* reading is what ends it — for this mounting and for every later
     one, here and after a reload of the same build */
  const close = () => {
    rememberBuild();
    setIsOpen(false);
  };

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }

    /* Opening a modal hands the focus to the first thing in it that
       can be operated — here the link at its very foot. A phone
       scrolls down to it, so the news the dialog came to show starts
       out of sight, and the link wears the ring of a focus nobody
       asked for. The heading takes it instead: it is where the
       reading begins and what a screen reader should announce. The
       scroll the browser has already done is undone with it. */
    headingRef.current?.focus({ preventScroll: true });
    dialog.scrollTop = 0;

    const handleCancel = (event: Event) => {
      event.preventDefault();
      rememberBuild();
      setIsOpen(false);
    };
    dialog.addEventListener('cancel', handleCancel);
    return () => dialog.removeEventListener('cancel', handleCancel);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <dialog
      ref={dialogRef}
      className="whats-new-dialog"
      aria-label={t('overview.whatsNew.title')}
    >
      {/* takes the focus when the dialog opens; a heading is nothing
          to operate, so it needs to be told it can hold one */}
      <h2 ref={headingRef} tabIndex={-1}>
        {t('overview.whatsNew.title')}
      </h2>
      <p className="whats-new-dialog-lead">{t('overview.whatsNew.updated')}</p>

      {/* the way out and the way on share the closing line of the
          list: below one another they read as two separate offers */}
      <WhatsNewList onFollowMore={close}>
        <button
          type="button"
          className="btn-close btn-labelled"
          onClick={close}
        >
          {t('common.close')}
        </button>
      </WhatsNewList>
    </dialog>
  );
}
